import React, { useState, useEffect, useCallback, useRef, memo, startTransition } from 'react';
import SafeSuspense from '~/components/safe-suspense';

const instantSearchImport = () => import('~/components/instant-search-results');
const InstantSearchResults = React.lazy(instantSearchImport);

interface SearchBarProps {
  id: string;
  onSubmit: (query: string) => void;
  className?: string;
  tabIndex?: number;
  onSearchClose?: () => void;
}

export default memo(function SearchBar({
  id,
  onSubmit,
  className,
  tabIndex,
  onSearchClose,
}: SearchBarProps) {
  const [value, setValue] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Preload the InstantSearchResults chunk during idle time so the first
  // search doesn't pay the chunk-download + parse cost (eliminates cold-start INP spike).
  useEffect(() => {
    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(() => instantSearchImport());
      return () => cancelIdleCallback(id);
    } else {
      const timer = setTimeout(instantSearchImport, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Cleanup debounce timer on unmount only
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Notify carousels to pause smooth-scroll animations while search results
  // are visible — the animations block the main thread and cause INP spikes.
  // This covers desktop (where showMobileSearch never toggles) and mobile.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('search-toggle', { detail: { open: committedQuery.trim().length > 0 } }),
    );
  }, [committedQuery]);

  // Debounce is handled directly in onChange — no useEffect per keystroke.
  // This avoids effect cleanup/setup overhead on every character typed
  // (which StrictMode triples in dev: mount → unmount → remount).
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (newValue.trim().length < 1) {
      startTransition(() => setCommittedQuery(''));
    } else {
      debounceRef.current = setTimeout(
        () => startTransition(() => setCommittedQuery(newValue)),
        150,
      );
    }
  }, []);

  const handleClose = useCallback(() => {
    setValue('');
    setCommittedQuery('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    inputRef.current?.blur();
    onSearchClose?.();
  }, [onSearchClose]);

  // Close search when clicking outside the input and outside the results overlay
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!inputRef.current) return;
      // Only act when the input is focused
      if (document.activeElement !== inputRef.current) return;
      const target = e.target as HTMLElement;
      // Click is inside the input — ignore
      if (inputRef.current.contains(target)) return;
      // Click is inside the portaled search results dialog — let its own handlers manage it
      if (target.closest?.('[role="dialog"][aria-label="Search results"]')) return;
      // Click is on a search toggle button — let the button's onClick handle it
      if (target.closest?.('[aria-controls="searchwrapmobile"]')) return;
      handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClose]);

  return (
    <>
      <label htmlFor={id} className="sr-only">
        Search input
      </label>
      <input
        ref={inputRef}
        id={id}
        type="search"
        placeholder="Search Products..."
        className={className}
        value={value}
        onChange={handleChange}
        onFocus={instantSearchImport}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            onSubmit(value);
            setValue('');
            setCommittedQuery('');
            if (debounceRef.current) clearTimeout(debounceRef.current);
          }
          // Tab into the portaled search results (portal breaks natural DOM tab order)
          if (e.key === 'Tab' && !e.shiftKey && committedQuery) {
            const dialog = document.querySelector('[role="dialog"][aria-label="Search results"]');
            if (dialog) {
              const firstFocusable = dialog.querySelector('a, button, [tabindex="0"]') as HTMLElement;
              if (firstFocusable) {
                e.preventDefault();
                firstFocusable.focus();
              }
            }
          }
        }}
        aria-describedby="search-help"
        aria-expanded={value.trim().length > 0}
        aria-autocomplete="list"
        role="combobox"
        tabIndex={tabIndex}
      />
      <SafeSuspense
        fallback={null}
      >
        {committedQuery ? (
          <InstantSearchResults
            query={committedQuery}
            onCloseResults={handleClose}
          />
        ) : null}
      </SafeSuspense>
    </>
  );
});
